/**
 * LEDGER — Vinnie's register: the debt, the $100 terminal note and the
 * per-day interest the world clock charges at the daily close (ADR-0020/0022).
 *
 * The view owns no numbers. It reads the debt and the v2 day marker from
 * `GET /core/state` and the notes from `GET /core/loans`, then renders them.
 * The per-day interest shown is the backend's own rounding of the loan's
 * server rate over its server balance — the same formula the L4c close uses —
 * never a client-side price or a guessed rate.
 *
 * Degrades cleanly: offline, v2 disabled, no debt and no notes all render an
 * honest empty state instead of invented values.
 */
import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

export const LOAN_PRESETS = ['50', '100', '250', '500'];

const state = { loans: [], loaded: false, loading: false, error: null, notice: '', preset: '100' };

let loadPromise = null;

export function _resetLedgerState(overrides = {}) {
  Object.assign(
    state,
    { loans: [], loaded: false, loading: false, error: null, notice: '', preset: '100' },
    overrides,
  );
  loadPromise = null;
}

// Backwards-compatible alias: the old Vinnie view is now the Ledger app.
export const _resetVinnieState = _resetLedgerState;

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function coreSnapshot() {
  const session = useCoreSession.getState();
  return { ...(session.state || {}), status: session.status };
}

const PARTIAL_REPAY = 25;

/** Cents from the server money shape (`{ amount: "12.34" }`) or a raw value. */
export function parseMoneyMinor(money) {
  const raw = money && typeof money === 'object' ? money.amount : money;
  if (raw == null || raw === '') return null;
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100);
}

export function formatMoneyMinor(minor) {
  if (minor == null || !Number.isFinite(Number(minor))) return '--';
  const absolute = Math.abs(Math.round(Number(minor)));
  const sign = Number(minor) < 0 ? '-' : '';
  return `${sign}$${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}

/**
 * One day of compound interest, rounded exactly like the backend
 * (`economy._round_bps`: half-up to the cent).
 */
export function nextDayInterestMinor(amountDueMinor, rateBps) {
  const amount = Number(amountDueMinor);
  const bps = Number(rateBps);
  if (!Number.isFinite(amount) || !Number.isFinite(bps) || amount <= 0 || bps <= 0) {
    return 0;
  }
  return Math.floor((amount * bps + 5_000) / 10_000);
}

/** The starting note is the oldest note on file. */
export function selectTerminalNote(loans) {
  const list = Array.isArray(loans) ? loans.filter(Boolean) : [];
  if (!list.length) return null;
  const ordered = [...list].sort((a, b) => {
    const left = String(a.createdAt || '');
    const right = String(b.createdAt || '');
    if (left === right) return 0;
    return left < right ? -1 : 1;
  });
  return ordered[0];
}

/**
 * The v2 day marker (`ticket`, null or not) is only present on
 * `GET /core/state` when `DCW_CORE_V2_ENABLED` is on. That is how the client
 * distinguishes a per-day rate from the legacy per-turn rate.
 */
export function isV2State(core) {
  return Boolean(core && typeof core === 'object' && Object.prototype.hasOwnProperty.call(core, 'ticket'));
}

export function summarizeLedger(core, loans = []) {
  const note = selectTerminalNote(loans);
  const debtMinor = parseMoneyMinor(core?.debt) ?? 0;
  const v2 = isV2State(core);
  const noteDueMinor = parseMoneyMinor(note?.amountDue) ?? (note ? null : debtMinor || null);
  const rateBps = Number(note?.rateBps ?? 0) || null;
  const activeLoans = (Array.isArray(loans) ? loans : []).filter(
    (loan) => String(loan?.status || '').toLowerCase() === 'active',
  ).length;
  return {
    v2,
    debtMinor,
    cashMinor: parseMoneyMinor(core?.cash) ?? 0,
    note,
    noteDueMinor,
    noteStatus: note ? String(note.status || '--').toUpperCase() : debtMinor > 0 ? 'ON FILE' : 'NONE',
    rateBps,
    interestMinor: v2 && note && rateBps ? nextDayInterestMinor(noteDueMinor, rateBps) : null,
    accrued: note?.accruedTurns ?? null,
    activeLoans,
    totalNotes: Array.isArray(loans) ? loans.length : 0,
  };
}

export function ledgerErrorMessage(error) {
  if (!error) return 'LEDGER LINE DOWN';
  if (error.code === 'CORE-404-012') return 'LEDGER LINE CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

export function deriveActiveLoan(loans, core) {
  const listed = loans.find((loan) => loan.status === 'active') || null;
  if (listed) return listed;
  if (core?.status === 'online' && moneyAmount(core.debt) > 0) {
    return {
      loanId: null,
      principal: { amount: core.debt?.amount },
      amountDue: core.debt,
    };
  }
  return null;
}

function activeLoan() {
  return deriveActiveLoan(state.loans, coreSnapshot());
}

export async function loadLedgerData() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const session = useCoreSession.getState();
      if (typeof session.refreshState === 'function') {
        await session.refreshState();
      }
      state.loans = (await useCoreSession.getState().loadLoans()) || [];
      state.loaded = true;
    } catch (error) {
      state.error = ledgerErrorMessage(error);
      state.loaded = true;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // LEDGER', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'DEBT // TERMINAL NOTE // INTEREST', 200, 4, TERMINAL.TEXT_DIM);
  text(ctx, online ? 'LEDGER' : 'OFFLINE', safe.width - 56, 4, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function rateLabel(summary) {
  if (!summary.rateBps) return '--';
  const unit = summary.v2 ? 'DAY' : 'TURN';
  return `${(summary.rateBps / 100).toFixed(2)}% / ${unit}`;
}

function interestLabel(summary) {
  if (!summary.v2) return 'V2 OFFLINE';
  if (summary.interestMinor == null) return '--';
  return `+${formatMoneyMinor(summary.interestMinor)}`;
}

function drawRegister(ctx, x, y, w, h, compact = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'LEDGER');
  const core = coreSnapshot();
  const summary = summarizeLedger(core, state.loans);
  const rowStep = compact ? 15 : 20;
  let cursor = y + 30;
  const row = (label, value, color = TERMINAL.TEXT) => {
    text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
    text(ctx, value, x + (compact ? 116 : 140), cursor, color);
    cursor += rowStep;
  };

  const debtColor = summary.debtMinor > 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT;
  row('DEBT', formatMoneyMinor(summary.debtMinor), debtColor);
  row(
    'TERMINAL NOTE',
    summary.noteDueMinor == null ? 'NONE' : formatMoneyMinor(summary.noteDueMinor),
    summary.note ? TERMINAL.TEXT : TERMINAL.TEXT_DIM,
  );
  row('NOTE STATUS', summary.noteStatus, TERMINAL.TEXT_DIM);
  row('CASH', core.status === 'online' ? formatMoneyMinor(summary.cashMinor) : '--');
  row('RATE', rateLabel(summary), summary.v2 ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
  row('NEXT DAY', interestLabel(summary), summary.v2 ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
  row(
    'ACCRUED',
    summary.accrued == null ? '--' : `${summary.accrued} ${summary.v2 ? 'DAYS' : 'TURNS'}`,
    TERMINAL.TEXT_DIM,
  );

  if (!compact) {
    const principal = parseMoneyMinor(summary.note?.principal);
    row('PRINCIPAL', principal == null ? '--' : formatMoneyMinor(principal));
    row('TERM', summary.note?.termTurns ? `${summary.note.termTurns} DAYS` : '--');
    row('NOTES', `${summary.activeLoans} ACTIVE / ${summary.totalNotes} ON FILE`);
  }

  rule(ctx, x + 12, cursor + 2, w - 24);
  cursor += compact ? 12 : 16;
  let message = state.error || state.notice;
  if (!message) {
    if (core.status !== 'online') message = 'NO LINE TO THE HOUSE.';
    else if (summary.debtMinor <= 0 && !summary.note) message = 'NO DEBT ON THE BOOKS.';
    else if (summary.debtMinor <= 0) message = 'TERMINAL NOTE SETTLED.';
    else message = 'THE HOUSE KEEPS THE LEDGER.';
  }
  text(ctx, String(message).slice(0, compact ? 36 : 46), x + 12, cursor, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
  if (!compact) {
    text(ctx, 'INTEREST POSTS AT THE DAILY CLOSE.', x + 12, cursor + 18, TERMINAL.TEXT_DIM);
  }
}

function drawActions(ctx, x, y, w, h, compact = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'TERMS');
  const loan = activeLoan();
  const core = coreSnapshot();
  const online = core.status === 'online';
  const requested = Number(state.preset || 0);
  const canBorrow = online && !state.loading && moneyAmount(core.debt) + requested <= 1000;
  const due = parseMoneyMinor(loan?.amountDue) ?? 0;
  const canRepay = online && Boolean(loan?.loanId) && !state.loading && due > 0;
  const canPartial = canRepay && due > PARTIAL_REPAY * 100;

  const termsY = 22;
  text(ctx, 'BORROW 1.00% / DAY', x + 12, y + termsY, TERMINAL.TEXT_DIM);
  text(ctx, 'COMPOUNDS EACH WORLD DAY', x + 12, y + termsY + 18, TERMINAL.TEXT_DIM);
  text(ctx, 'DEFAULT 10% PENALTY', x + 12, y + termsY + 36, TERMINAL.TEXT_DIM);

  const pad = 12;
  const gap = compact ? 6 : 4;
  const cols = 4;
  const buttonH = compact ? 44 : 26;
  const gridW = (w - pad * 2 - gap * (cols - 1)) / cols;
  const gridTop = y + 72;
  const selected = LOAN_PRESETS.includes(state.preset);
  LOAN_PRESETS.forEach((preset, index) => {
    const px = x + pad + (index % cols) * (gridW + gap);
    const py = gridTop + Math.floor(index / cols) * (buttonH + gap);
    DopeUI.Button(
      ctx,
      px,
      py,
      gridW,
      buttonH,
      `$${preset}`,
      canBorrow && state.preset === preset ? 'hover' : canBorrow ? 'normal' : 'disabled',
    );
    if (canBorrow) {
      interactiveRegistry.register({
        id: `vinnie-preset-${preset}`,
        x: px,
        y: py,
        w: gridW,
        h: buttonH,
        onClick: () => {
          state.preset = preset;
        },
      });
    }
  });

  const bottomY = y + h - 12;
  const commsY = bottomY - buttonH;
  const partialY = commsY - buttonH - gap;
  const repayY = partialY - buttonH - gap;
  const borrowY = repayY - buttonH - gap;

  const presetLabel = selected ? state.preset : LOAN_PRESETS[1];
  DopeUI.Button(
    ctx,
    x + 12,
    borrowY,
    w - 24,
    buttonH,
    state.loading ? 'WAIT' : `BORROW $${presetLabel}`,
    canBorrow ? 'hover' : 'disabled',
  );

  if (canRepay) {
    const fullLabel = `REPAY FULL ${formatMoneyMinor(due)}`;
    DopeUI.Button(ctx, x + 12, repayY, w - 24, buttonH, state.loading ? 'WAIT' : fullLabel, 'hover');
    if (canPartial) {
      DopeUI.Button(ctx, x + 12, partialY, w - 24, buttonH, `REPAY $${PARTIAL_REPAY}`, 'normal');
    }
  } else {
    DopeUI.Button(ctx, x + 12, repayY, w - 24, buttonH, 'REPAY FULL $0.00', 'disabled');
    DopeUI.Button(ctx, x + 12, partialY, w - 24, buttonH, `REPAY $${PARTIAL_REPAY}`, 'disabled');
  }
  DopeUI.Button(ctx, x + 12, commsY, w - 24, buttonH, 'OPEN COMMS', 'normal');

  if (canBorrow) {
    interactiveRegistry.register({
      id: 'vinnie-borrow',
      x: x + 12,
      y: borrowY,
      w: w - 24,
      h: buttonH,
      onClick: async () => {
        if (state.loading) return;
        state.loading = true;
        state.error = null;
        try {
          const amount = selected ? state.preset : '100';
          await useCoreSession.getState().openLoan(amount);
          state.notice = `LOAN OPEN // $${amount}.00`;
          await loadLedgerData();
        } catch (error) {
          state.error = ledgerErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
  if (canRepay) {
    interactiveRegistry.register({
      id: 'vinnie-repay',
      x: x + 12,
      y: repayY,
      w: w - 24,
      h: buttonH,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          await useCoreSession.getState().repayLoan(loan.loanId, (due / 100).toFixed(2));
          state.notice = `REPAYMENT SENT // ${formatMoneyMinor(due)}`;
          await loadLedgerData();
        } catch (error) {
          state.error = ledgerErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
    if (canPartial) {
      interactiveRegistry.register({
        id: 'vinnie-repay-partial',
        x: x + 12,
        y: partialY,
        w: w - 24,
        h: buttonH,
        onClick: async () => {
          state.loading = true;
          state.error = null;
          try {
            await useCoreSession.getState().repayLoan(loan.loanId, String(PARTIAL_REPAY));
            state.notice = `REPAYMENT SENT // $${PARTIAL_REPAY}.00`;
            await loadLedgerData();
          } catch (error) {
            state.error = ledgerErrorMessage(error);
          } finally {
            state.loading = false;
          }
        },
      });
    }
  }
  interactiveRegistry.register({
    id: 'vinnie-comms',
    x: x + 12,
    y: commsY,
    w: w - 24,
    h: buttonH,
    onClick: () => navigateTo('comms_window'),
  });
}

function renderLedger(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadLedgerData();
  }

  drawTop(ctx, safe, session);
  const mobile = safe.width < 500;

  if (session.status !== 'online') {
    DopeUI.Panel(ctx, 8, 36, safe.width - 16, 120, 'NO LINE');
    text(ctx, 'LINE DOWN. LOCAL SHELL.', 20, 70, TERMINAL.TEXT_DIM);
    text(ctx, 'THE LEDGER WAITS. NO FALSE NUMBERS.', 20, 92, TERMINAL.TEXT_DIM);
  } else if (mobile) {
    drawRegister(ctx, 8, 32, safe.width - 16, 200, true);
    drawActions(ctx, 8, 240, safe.width - 16, 314, true);
  } else {
    drawRegister(ctx, 8, 36, 376, 280);
    drawActions(ctx, 400, 36, 232, 300);
  }

  const footerY = mobile ? 566 : 344;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER   ESC DESKTOP', 8, mobile ? footerY + 8 : footerY + 4, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 578 : 368;
  const backW = mobile ? safe.width - 16 : 192;
  const backH = mobile ? 44 : 24;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'vinnie-back',
    x: backX,
    y: backY,
    w: backW,
    h: backH,
    onClick: () => navigateTo('main_menu'),
  });
  if (!mobile) {
    text(ctx, 'VINNIE // THE HOUSE ALWAYS KEEPS A LEDGER.', 8, 384, TERMINAL.GREEN);
  }
}

registerView('ledger', renderLedger);
// The previous app id opened the `vinnie` view; keep the alias so deep links
// and the existing shell tests keep working.
registerView('vinnie', renderLedger);

export default 'ledger';
